import { useEffect, useMemo } from "react";

import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap
} from "react-leaflet";

import L from "leaflet";

import "leaflet/dist/leaflet.css";
import "./ChargerMap.css";

import { chargerCoordinates } from "./ChargerCoordinates";

// Import Leaflet's default marker images.
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Replace Leaflet's default icon paths with the imported image files.
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow
});

// Custom marker used to identify AC charging locations.
const acIcon = L.divIcon({
  className: "charger-map-marker-wrapper",
  html: '<span class="charger-map-marker charger-map-marker-ac">AC</span>',
  iconSize: [38, 38],
  iconAnchor: [19, 19],
  popupAnchor: [0, -20]
});

// Custom marker used to identify DC charging locations.
const dcIcon = L.divIcon({
  className: "charger-map-marker-wrapper",
  html: '<span class="charger-map-marker charger-map-marker-dc">DC</span>',
  iconSize: [38, 38],
  iconAnchor: [19, 19],
  popupAnchor: [0, -20]
});

// Updates the map position and zoom level based on the
// charging locations currently visible on the map.
function MapBounds({ visibleLocations }) {
  const map = useMap();

  useEffect(() => {
    if (visibleLocations.length === 0) {
      return;
    }

    // If only one location is visible, centre the map directly on it.
    if (visibleLocations.length === 1) {
      const location = visibleLocations[0];

      map.setView(
        [location.latitude, location.longitude],
        15,
        {
          animate: true
        }
      );

      return;
    }

    // If multiple locations are visible, calculate bounds that
    // contain every marker.
    const bounds = L.latLngBounds(
      visibleLocations.map((location) => [
        location.latitude,
        location.longitude
      ])
    );

    map.fitBounds(bounds, {
      padding: [45, 45],
      maxZoom: 15
    });
  }, [map, visibleLocations]);

  return null;
}


function ChargerMap({
  isOpen,
  onClose,
  locations = [],
  selectedLocations = [],
  chargerType = "Both"
}) {

  // Determine which charging locations should appear on the map.
  //
  // The locations prop comes directly from the backend /locations
  // endpoint, so the coordinate file is never used as the source
  // of which stations exist. It only supplies coordinates for
  // stations that the backend has confirmed contain charging data.
  const locationsToDisplay = useMemo(() => {

    // First restrict the backend locations by AC/DC charger type.
    const allowedLocations = locations.filter((location) => {
      if (chargerType === "Both") {
        return true;
      }

      return location.type === chargerType;
    });

    // An empty selectedLocations array now means that the user has
    // selected no locations. Do not interpret it as "show all".
    const selected = allowedLocations.filter((location) =>
      selectedLocations.includes(location.chargingStationId)
    );

    // ChargerCoordinates only supplies the physical coordinates.
    // A coordinate entry cannot create a map marker by itself.
    //
    // Locations without verified coordinates are omitted from the map.
    return selected
      .map((location) => {
        const coordinates =
          chargerCoordinates[location.locationName];

        if (!coordinates) {
          return null;
        }

        return {
          ...location,
          ...coordinates
        };
      })
      .filter(Boolean);

  }, [
    locations,
    selectedLocations,
    chargerType
  ]);


  // Number of selected backend locations for the current charger type.
  // This includes selected stations that may not yet have map coordinates.
  const selectedDataLocations = useMemo(() => {
    return locations.filter((location) => {
      const matchesChargerType =
        chargerType === "Both" ||
        location.type === chargerType;

      const isSelected =
        selectedLocations.includes(
          location.chargingStationId
        );

      return matchesChargerType && isSelected;
    });
  }, [
    locations,
    selectedLocations,
    chargerType
  ]);


  // Handles behaviour while the map modal is open.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // Allow the user to close the map using the Escape key.
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    document.body.classList.add(
      "map-modal-open"
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleEscape
      );

      document.body.classList.remove(
        "map-modal-open"
      );
    };
  }, [isOpen, onClose]);


  // Do not render the map modal when it is closed.
  if (!isOpen) {
    return null;
  }


  // Use the first available charger as the initial map centre.
  // Perth is used as a fallback if no mapped locations are available.
  const defaultCentre =
    locationsToDisplay.length > 0
      ? [
          locationsToDisplay[0].latitude,
          locationsToDisplay[0].longitude
        ]
      : [-31.9523, 115.8613];


  return (
    <div
      className="charger-map-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="charger-map-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="charger-map-title"
      >

        {/* Map modal heading and close button */}
        <header className="charger-map-header">

          <div>
            <span className="charger-map-eyebrow">
              Charging network
            </span>

            <h2 id="charger-map-title">
              Charger locations
            </h2>

            <p>
              {selectedDataLocations.length === 0
                ? "No charging locations selected."
                : `Showing ${locationsToDisplay.length} mapped ${
                    locationsToDisplay.length === 1
                      ? "location"
                      : "locations"
                  } from ${selectedDataLocations.length} selected ${
                    selectedDataLocations.length === 1
                      ? "location"
                      : "locations"
                  }.`}
            </p>
          </div>


          <button
            type="button"
            className="charger-map-close"
            onClick={onClose}
            aria-label="Close charger map"
          >
            ×
          </button>

        </header>


        <div className="charger-map-body">

          {/* Only render the map if selected locations with
              verified coordinates are available. */}
          {locationsToDisplay.length > 0 ? (

            <MapContainer
              center={defaultCentre}
              zoom={11}
              scrollWheelZoom
              className="charger-map"
            >

              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />


              <MapBounds
                visibleLocations={locationsToDisplay}
              />


              {locationsToDisplay.map((location) => {

                const googleMapsUrl =
                  "https://www.google.com/maps/search/?api=1&query=" +
                  encodeURIComponent(
                    location.googleMapsQuery ||
                    location.address ||
                    location.locationName
                  );


                return (
                  <Marker
                    key={`${location.chargingStationId}-${location.type}`}
                    position={[
                      location.latitude,
                      location.longitude
                    ]}
                    icon={
                      location.type === "DC"
                        ? dcIcon
                        : acIcon
                    }
                  >

                    <Popup>
                      <div className="charger-map-popup">

                        <strong>
                          {location.locationName}
                        </strong>


                        <span
                          className={`charger-map-popup-badge ${
                            location.type === "DC"
                              ? "charger-map-popup-badge-dc"
                              : "charger-map-popup-badge-ac"
                          }`}
                        >
                          {location.type} charger
                        </span>


                        {location.address && (
                          <p>
                            {location.address}
                          </p>
                        )}


                        <a
                          href={googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open in Google Maps
                        </a>

                      </div>
                    </Popup>

                  </Marker>
                );
              })}

            </MapContainer>

          ) : (

            <div className="charger-map-empty">

              <h3>
                {selectedDataLocations.length === 0
                  ? "No locations selected"
                  : "No mapped locations available"}
              </h3>

              <p>
                {selectedDataLocations.length === 0
                  ? "Select one or more charging locations to display them on the map."
                  : "The selected charging locations have data, but their physical coordinates have not yet been added to the map."}
              </p>

            </div>

          )}

        </div>


        {/* Map legend and close button */}
        <footer className="charger-map-footer">

          <div className="charger-map-legend">

            <span>
              <i className="charger-map-legend-ac" />
              AC
            </span>

            <span>
              <i className="charger-map-legend-dc" />
              DC
            </span>

          </div>


          <button
            type="button"
            className="charger-map-done"
            onClick={onClose}
          >
            Close map
          </button>

        </footer>

      </section>
    </div>
  );
}

export default ChargerMap;